import React, { useState } from "react";
import { Formik, useFormikContext } from "formik";
import { useQuery } from "@apollo/client/react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

import DetailRow from "src/components/card/DetailRow";
import MovewareStorageAccountField from "src/components/modals/ProjectActions/MovewareStorageAccountField";
import {
  LINK_EXTERNAL_RECORD_MUTATION,
  UNLINK_EXTERNAL_RECORD_MUTATION,
} from "src/constants/graphql/mutations";
import { FETCH_PROJECT_QUERY, PROJECT_INTEGRATION_RECORDS_QUERY } from "src/constants/graphql/queries";
import useAuth from "src/hooks/useAuth";
import useRoles from "src/hooks/user/useRoles";
import useMutation from "src/hooks/useMutation";
import useNotification from "src/hooks/notification";

const present = value => value !== null && value !== undefined && value !== "";

const formatStatus = status =>
  status ? `${status.charAt(0).toUpperCase()}${status.slice(1)}` : null;

const formatDate = date => {
  const match = date?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : date;
};

const withStatus = (value, status) => {
  if (!present(value)) return null;
  const formattedStatus = formatStatus(status);
  return formattedStatus ? `${value} (${formattedStatus})` : value;
};

function LinkedMovewareDetails({ record }) {
  const details = record.data || {};
  const customerId = details.customer_id || details.customer_number;
  const leftRows = [
    ["Branch", details.branch],
    ["Customer Name", details.customer_name],
    ["Customer ID", withStatus(customerId, details.customer_status)],
    ["Storage Account Name", details.storage_account_name],
    ["Storage Account ID", withStatus(record.externalId, details.storage_account_status || record.status)],
  ].filter(([, value]) => present(value));
  const rightRows = [
    ["Date In", formatDate(details.date_in)],
    ["Order Number", details.order_number],
    ["Reference In", details.move_id],
  ].filter(([, value]) => present(value));

  return (
    <Grid container columnSpacing={4} rowSpacing={0.75} sx={{ pt: 1.5 }}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {leftRows.map(([label, value]) => (
            <DetailRow key={label} label={label} value={value} />
          ))}
        </Box>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {rightRows.map(([label, value]) => (
            <DetailRow key={label} label={label} value={value} />
          ))}
        </Box>
        {present(details.notes) && (
          <Box sx={{ mt: 0.75 }}>
            <Typography variant="body2" color="text.secondary">
              Notes
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {details.notes}
            </Typography>
          </Box>
        )}
      </Grid>
    </Grid>
  );
}

function UnlinkedMovewareLookup({ projectId, confirmLabel, onLinked }) {
  const { notifySuccess, notifyError } = useNotification();
  const [linkExternalRecord, { loading }] = useMutation(LINK_EXTERNAL_RECORD_MUTATION, {
    refetchQueries: [
      { query: PROJECT_INTEGRATION_RECORDS_QUERY, variables: { id: projectId } },
      { query: FETCH_PROJECT_QUERY, variables: { id: projectId } },
    ],
    awaitRefetchQueries: true,
  });

  const handleConfirm = storageAccount =>
    new Promise((resolve, reject) => {
      linkExternalRecord({
        variables: {
          projectId,
          externalRecordId: storageAccount.externalRecordId,
        },
        onSuccess: ({ project }) => {
          notifySuccess("Moveware storage account linked");
          onLinked(project);
          resolve();
        },
        onFailure: errors => {
          notifyError(Object.keys(errors).length ? errors : "Unable to link this storage account");
          reject(errors);
        },
      });
    });

  return (
    <Formik
      initialValues={{ movewareStorageAccountId: "", externalRecordId: "" }}
      onSubmit={() => {}}
    >
      <Grid container spacing={1} sx={{ pt: 1.5 }}>
        <Grid size={12}>
          <Typography variant="body2" color="text.secondary">
            Look up a Moveware storage account ID to link it to this customer.
          </Typography>
        </Grid>
        <MovewareStorageAccountField
          confirmLabel={loading ? "Linking…" : confirmLabel}
          confirming={loading}
          onConfirm={handleConfirm}
        />
      </Grid>
    </Formik>
  );
}

export default function MovewareDetails({ projectId }) {
  const { user } = useAuth();
  const { setFieldValue } = useFormikContext();
  const isAdmin = useRoles(["admin", "super_admin"]);
  const movewareEnabled = user.company.enabledIntegrationProviders?.includes("moveware");
  const readOnly = user.role === "billing_user";
  const [editing, setEditing] = useState(false);
  const { data, loading } = useQuery(PROJECT_INTEGRATION_RECORDS_QUERY, {
    variables: { id: projectId },
    skip: !movewareEnabled || !projectId,
  });
  const record = data?.project?.externalRecords?.find(
    externalRecord => externalRecord.recordType === "storage_account"
  );
  const { notifySuccess, notifyError } = useNotification();
  const [unlinkExternalRecord, { loading: unlinking }] = useMutation(UNLINK_EXTERNAL_RECORD_MUTATION, {
    refetchQueries: [
      { query: PROJECT_INTEGRATION_RECORDS_QUERY, variables: { id: projectId } },
      { query: FETCH_PROJECT_QUERY, variables: { id: projectId } },
    ],
    awaitRefetchQueries: true,
  });

  if (!movewareEnabled) return null;

  const applyLinkedFields = project => {
    if (!project) return;
    setFieldValue("orderNumber", project.orderNumber || "");
    setFieldValue("customerPoNumber", project.customerPoNumber || "");
    setFieldValue("customerNotes", project.customerNotes || "");
    setEditing(false);
  };

  const handleRemove = () => {
    unlinkExternalRecord({
      variables: { projectId },
      onSuccess: ({ project }) => {
        notifySuccess("Moveware storage account removed");
        applyLinkedFields(project);
      },
      onFailure: errors => {
        notifyError(Object.keys(errors).length ? errors : "Unable to remove this storage account");
      },
    });
  };

  return (
    <Paper sx={{ mt: 2 }}>
      <Box p={2}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography fontWeight={500}>Moveware Details</Typography>
          {record && isAdmin && !editing && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" variant="outlined" onClick={() => setEditing(true)}>
                Change
              </Button>
              <Button size="small" color="error" variant="outlined" disabled={unlinking} onClick={handleRemove}>
                {unlinking ? "Removing…" : "Remove"}
              </Button>
            </Box>
          )}
        </Box>
        {loading && !data ? null : record && !editing ? (
          <LinkedMovewareDetails record={record} />
        ) : readOnly && !record ? (
          <Typography sx={{ pt: 1.5 }} variant="body2" color="text.secondary">
            No Moveware storage account is linked to this customer.
          </Typography>
        ) : editing || !record ? (
          <>
            {editing && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
                <Button size="small" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              </Box>
            )}
            <UnlinkedMovewareLookup
              projectId={projectId}
              confirmLabel={editing ? "Replace Account" : "Confirm Account"}
              onLinked={applyLinkedFields}
            />
          </>
        ) : null}
      </Box>
    </Paper>
  );
}
